import React, { useState, useEffect, useContext, useMemo } from "react";
import { Button, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useQuery, gql } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import AddShiftOffCanvasWizard from "../../components/Wizards/AddShiftOffCanvasWizard";
import ShiftWeekCalendar from "../../components/Shifts/ShiftWeekCalendar";
import "../../starek.css";
import DataTable from "react-data-table-component";

const DAYS_OF_WEEK = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const formatTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

const runsOnToDays = (mask) => {
    const days = [];
    DAYS_OF_WEEK.forEach((day, i) => {
        if (mask & (1 << i)) days.push(day.slice(0, 3));
    });
    return days.length ? days.join(", ") : "-";
};

const parseShiftData = (shifts) => {
    const dayShifts = DAYS_OF_WEEK.map((day) => ({ day, ranges: [] }));
    shifts.forEach((shift) => {
        const { start_time, end_time, runs_on } = shift;
        DAYS_OF_WEEK.forEach((_, index) => {
            if (runs_on & (1 << index)) {
                dayShifts[index].ranges.push({
                    shift_id: shift.id,
                    shift_name: shift.name,
                    start: start_time,
                    end: end_time,
                });
            }
        });
    });
    return dayShifts;
};

const GET_SHIFT_LIST_FOR_PLANT = gql`
    query GetShifts($plantId: Int!) {
        shifts(where: { plant_id: { equals: $plantId } }) {
            id
            name
            description
            start_time
            end_time
            runs_on
            created_at
        }
    }
`;

const DELETE_MANY_SHIFTS = gql`
    mutation DeleteManyShift($ids: [Int!]!) {
        deleteManyShift(where: { id: { in: $ids } }) {
            count
        }
    }
`;
const DELETE_ONE_SHIFT = gql`
    mutation DeleteOneShift($id: Int!) {
        deleteOneShift(where: { id: { equals: $id } }) {
            id
        }
    }
`;

const Shifts = () => {
    const starekContext = useContext(StarekContext);

    const { data, loading, error, refetch, client } = useQuery(
        GET_SHIFT_LIST_FOR_PLANT,
        {
            client: starekContext.engineClient,
            variables: { plantId: starekContext.lastUsedPlant.id },
        }
    );

    const [items, setItems] = useState([]);
    const [dayShiftMapping, setDayShiftMapping] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [viewMode, setViewMode] = useState("timeline"); // "timeline" | "list"
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [showAddShiftDrawer, setShowAddShiftDrawer] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const isTimeline = viewMode === "timeline";

    useEffect(() => {
        if (data?.shifts) setItems(data.shifts);
    }, [data]);

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q)
        );
    }, [items, filterText]);

    useEffect(() => {
        setDayShiftMapping(parseShiftData(filteredItems));
    }, [filteredItems]);

    if (loading) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <div className="text-xs d-flex justify-content-center">
                    <i className="fa fa-clipboard fa-10x text-success"></i>
                </div>
                <Spinner />
            </div>
        );
    }
    if (error) {
        starekContext.showError(error);
    }

    const columns = [
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            minWidth: "130px",
            maxWidth: "200px",
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            sortable: true,
            grow: 1,
            cell: (r) => (
                <div className="text-truncate" style={{ maxWidth: 220 }}>
                    {r.description || "-"}
                </div>
            ),
        },
        {
            name: "Timings",
            selector: (r) =>
                `${formatTime(r.start_time)} - ${formatTime(r.end_time)}`,
            width: "400px",
            sortable: true,
        },
        {
            name: "Days",
            selector: (r) => runsOnToDays(r.runs_on),
            width: "400px",
        },
    ];

    const isAnySelected = selectedRows.length > 0;
    const isSingleSelected = selectedRows.length === 1;

    const handleAdd = () => {
        setEditingItem(null);
        setShowAddShiftDrawer(true);
    };

    const handleEdit = () => {
        if (!isSingleSelected) return;
        const item = selectedRows[0];
        setEditingItem(item);
        setShowAddShiftDrawer(true);
    };

    const handleBulkDelete = async () => {
        if (!isAnySelected) return;
        setIsDeleting(true);
        setDeleteDone(0);
        const ids = selectedRows.map((r) => r.id);

        try {
            await client.mutate({
                mutation: DELETE_MANY_SHIFTS,
                variables: { ids },
            });
            setDeleteDone(ids.length);
        } catch (_) {
            for (const id of ids) {
                try {
                    await client.mutate({
                        mutation: DELETE_ONE_SHIFT,
                        variables: { id },
                    });
                    setDeleteDone((d) => d + 1);
                } catch (err) {
                    starekContext.showError(err);
                    break;
                }
            }
        } finally {
            setIsDeleting(false);
            setToggleCleared((p) => !p);
            await refetch();
        }
    };

    return (
        <div className="container-fluid">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div
                    className="d-flex align-items-center flex-grow-1"
                    style={{ gap: 12 }}
                >
                    {!isTimeline && (
                        <input
                            type="text"
                            className="border rounded p-2"
                            style={{ width: "40%" }}
                            placeholder="Search"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    )}
                </div>
                <div
                    className="d-flex align-items-center tw-mr-4"
                    style={{ gap: 8 }}
                >
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Timeline View</Tooltip>}
                    >
                        <Button
                            variant={
                                viewMode === "timeline"
                                    ? "outline-primary"
                                    : "outline-secondary"
                            }
                            onClick={() => setViewMode("timeline")}
                            className="d-inline-flex align-items-center tw-h-10"
                        >
                            <i className="fa fa-calendar-alt tw-justify-center" />
                        </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>List View</Tooltip>}
                    >
                        <Button
                            variant={
                                viewMode === "list"
                                    ? "outline-primary"
                                    : "outline-secondary"
                            }
                            onClick={() => setViewMode("list")}
                            className="d-inline-flex align-items-center tw-h-10"
                        >
                            <i className="fa fa-list-ul" />
                        </Button>
                    </OverlayTrigger>
                </div>
                <div className="d-flex align-items-center">
                    {viewMode === "list" && (
                        <>
                            <Button
                                className="me-2"
                                variant={
                                    isSingleSelected
                                        ? "outline-primary"
                                        : "secondary"
                                }
                                disabled={!isSingleSelected || isDeleting}
                                onClick={handleEdit}
                            >
                                Edit
                            </Button>

                            <Button
                                className="me-2"
                                variant="danger"
                                onClick={handleBulkDelete}
                                disabled={isDeleting || !isAnySelected}
                                aria-live="polite"
                            >
                                {isDeleting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Deleting {deleteDone}/
                                        {selectedRows.length}
                                    </>
                                ) : (
                                    "Delete"
                                )}
                            </Button>
                        </>
                    )}

                    <Button variant="primary" onClick={handleAdd}>
                        Add a shift
                    </Button>
                </div>
            </div>

            {viewMode === "timeline" ? (
                <ShiftWeekCalendar
                    shifts={filteredItems}
                    onEventClick={(shift) => {
                        setEditingItem(shift);
                        setShowAddShiftDrawer(true);
                    }}
                />
            ) : (
                <DataTable
                    columns={columns}
                    data={filteredItems}
                    selectableRows
                    onSelectedRowsChange={({ selectedRows }) =>
                        setSelectedRows(selectedRows)
                    }
                    clearSelectedRows={toggleCleared}
                    highlightOnHover
                    pointerOnHover
                    responsive
                    pagination
                    dense
                    striped
                    customStyles={{
                        table: { style: { borderRadius: 6 } },
                        headRow: { style: { minHeight: "48px" } },
                        headCells: {
                            style: {
                                paddingTop: "12px",
                                paddingBottom: "12px",
                                paddingLeft: "16px",
                                paddingRight: "16px",
                            },
                        },
                        rows: { style: { minHeight: "44px" } },
                        cells: {
                            style: {
                                paddingTop: "12px",
                                paddingBottom: "12px",
                                paddingLeft: "16px",
                                paddingRight: "16px",
                                whiteSpace: "normal",
                                lineHeight: "1.2rem",
                            },
                        },
                    }}
                    selectableRowsHighlight
                    selectableRowsNoSelectAll={false}
                />
            )}

            <AddShiftOffCanvasWizard
                show={showAddShiftDrawer}
                closeOffcanvas={async () => {
                    setShowAddShiftDrawer(false);
                    setEditingItem(null);
                    await refetch();
                    setToggleCleared((p) => !p);
                }}
                starekContext={starekContext}
                item={editingItem || null}
            />
        </div>
    );
};

export { Shifts };
